import React, { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import MULTI_VOICE_DEMO_CONFIG from './multiVoiceDemoConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Core (always needed)
import { GoogleOAuthProvider } from '@react-oauth/google';
import EnhancedLanding from './EnhancedLanding';
import EnhancedAuthPage from './EnhancedAuthPage';
import EnhancedFooter from './EnhancedFooter';
import EnhancedUserMenu from './EnhancedUserMenu';
import SocialAuthButtons from './SocialAuthButtons';
import ChapterPreloader from './ChapterPreloader';

// Lazy-loaded (only when navigated to)
const EnhancedEmporium = lazy(() => import('./EnhancedEmporium'));
const EnhancedLibrary = lazy(() => import('./EnhancedLibrary'));
const EnhancedVoiceLibrary = lazy(() => import('./EnhancedVoiceLibrary'));
const EnhancedPricing = lazy(() => import('./EnhancedPricing'));
const HowItWorksPage = lazy(() => import('./HowItWorksPage'));
const TryFreePage = lazy(() => import('./TryFreePage'));
const ContactSupportPage = lazy(() => import('./ContactSupportPage'));
const EnhancedPlayerPage = lazy(() => import('./EnhancedPlayerPage'));
const EnhancedAdminPortal = lazy(() => import('./EnhancedAdminPortal'));
const EnhancedVoiceActorPortal = lazy(() => import('./EnhancedVoiceActorPortal'));
const EnhancedCreatorStudio = lazy(() => import('./EnhancedCreatorStudio'));
const EnhancedAccountManagement = lazy(() => import('./EnhancedAccountManagement'));
const EnhancedDocumentation = lazy(() => import('./EnhancedDocumentation'));
const EnhancedComingSoon = lazy(() => import('./EnhancedComingSoon'));
const EnhancedReferralPage = lazy(() => import('./EnhancedReferralPage'));
const EnhancedPaymentPortal = lazy(() => import('./EnhancedPaymentPortal'));
const DataDeletionPage = lazy(() => import('./DataDeletionPage'));
const AcceptableUsePolicyPage = lazy(() => import('./AcceptableUsePolicyPage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./CookiePolicyPage'));
const TermsOfUsePage = lazy(() => import('./TermsOfUsePage'));
const VoxSmythIconDemo = lazy(() => import('./components/VoxSmythIconDemo'));
const VoxSmythErrorPage = lazy(() => import('./VoxSmythErrorPage'));
const InvitationIntakePage = lazy(() => import('./InvitationIntakePage'));
const CreatorIntakePage = lazy(() => import('./CreatorIntakePage'));
const VoiceCloningGuide = lazy(() => import('./VoiceCloningGuide'));
const VoiceActorProfile = lazy(() => import('./VoiceActorProfile'));
const VoiceSharePage = lazy(() => import('./VoiceSharePage'));
const VocalMarketplace = lazy(() => import('./VocalMarketplace'));

// Non-lazy (small / needed for routing logic)
const { PaymentSuccessPage, PaymentCancelPage, PaymentHistoryPage } = require('./PaymentPages');
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const PaymentCancel = lazy(() => import('./PaymentCancel'));
const PlayerCharacterVoiceManager = lazy(() => import('./EnhancedPlayerCharacterVoiceManager'));
const VoiceActorDashboard = lazy(() => import('./VoiceActorDashboard'));
const AdminPortal = lazy(() => import('./AdminPortal'));
const CreatorDashboard = lazy(() => import('./CreatorDashboard'));
import InvitationChoicePage from './InvitationChoicePage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import OAuthLoginSuccess from './OAuthLoginSuccess';

// Utility functions
import VoxSmythUtils from './utils/VoxSmythUtils';
import VoxSmythIcons from './VoxSmythIcons';
import tokenService from './services/tokenService';
import './VoxSmythIcons.css';

// Hooks
import useCharacterData from './hooks/useCharacterData';
import useVoiceActorStatus from './hooks/useVoiceActorStatus';

// Route helpers (extracted)
import { LogoutHandler, EnhancedPageWrapper } from './routes/AppRouterHelpers';

// Mobile-only PWA chrome (bottom tab bar + persistent mini-player). Renders
// globally but CSS hides it on viewports >768px so desktop is untouched.
import MobileBottomNav from './components/MobileBottomNav';
import TrialCountdownBadge from './components/TrialCountdownBadge';
import LegalUpdateBanner from './components/LegalUpdateBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import { captureUtmParams } from './utils/utmCapture';

// Capture ad-campaign utm_* params on first load, before any client-side
// redirect can strip them (first touch wins for the session).
captureUtmParams();

// Persistent global audio engine — keeps playback alive across route changes.
import { PlayerProvider } from './contexts/PlayerContext';

// P2 PWA: route-level fade+slide transitions (mobile only).
import { motion, AnimatePresence } from 'framer-motion';

// App Router Component
const AppRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Automatic scroll to top on route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // Core application state
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [comingSoonTitle, setComingSoonTitle] = useState('');

  // Voice and audio state
  const [currentVoice, setCurrentVoice] = useState('rachel');
  const [currentBook, setCurrentBook] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiceConfiguration, setVoiceConfiguration] = useState({
    narratorVoice: 'rachel',
    characterAssignments: {},
    hasChanges: false
  });
  
  // Separate state for individual voice card demos
  const [individualVoicePlaying, setIndividualVoicePlaying] = useState(null); // null or voiceId
  const [individualVoiceLoading, setIndividualVoiceLoading] = useState(false);
  const [individualVoiceAudio, setIndividualVoiceAudio] = useState(null);
  const [lastSelectedVoice, setLastSelectedVoice] = useState(null); // Track last selected voice for pause/resume
  
  // Shared timepoint across all voices - they all continue from where the previous voice left off
  const [individualVoiceTimepoint, setIndividualVoiceTimepoint] = useState(0); // For display purposes
  const individualVoiceTimepointRef = useRef(0); // Global shared timepoint across all voices

  // BACKEND_URL is defined at module scope (see top of file)

  // Navigation helpers
  const navigateToHome = useCallback(() => navigate('/'), [navigate]);
  const navigateToAuth = useCallback(() => navigate('/auth'), [navigate]);
  const navigateToLibrary = useCallback(() => navigate('/library'), [navigate]);
  const navigateToDashboard = useCallback(() => navigate('/dashboard'), [navigate]);
  const navigateToPricing = useCallback(() => navigate('/pricing'), [navigate]);
  const navigateToVoices = useCallback(() => navigate('/voices'), [navigate]);
  const navigateToVoiceActorPortal = useCallback(() => navigate('/voice-actor-portal'), [navigate]);
  const navigateToMenu = useCallback(() => navigate('/menu'), [navigate]);
  const navigateToPlayer = useCallback((bookId = null) => {
    if (bookId) {
      navigate(`/player/${bookId}`);
    } else {
      navigate('/player');
    }
  }, [navigate]);

  // Authentication
  const fetchUserData = useCallback(async (_token) => {
    try {
      console.log('📡 Calling /api/user/profile (cookie auth)');
      // No Authorization header — the httpOnly cookie is sent automatically
      // by the global fetch interceptor (see services/authFetchSetup.js).
      const response = await fetch(`${BACKEND_URL}/api/user/profile`);

      console.log('📡 Profile response status:', response.status);

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ User profile data received:', userData);

        const user = {
          id: userData.user_id,
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          tier: userData.tier,
          subscription_status: userData.subscription_status,
          trial_ends_at: userData.trial_ends_at,
          created_at: userData.created_at
        };

        console.log('👤 Setting user state:', user);
        setUser(user);
      } else if (response.status === 401) {
        console.warn('🚫 Token is invalid, clearing auth data');
        tokenService.clearToken();
        setAuthToken(null);
        setUser(null);
      } else {
        console.error('❌ Profile fetch failed with status:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      console.log('🔐 Initializing authentication (cookie-based)...');
      // No localStorage token to read anymore — probe the httpOnly cookie by
      // hitting /api/user/profile. The fetch interceptor adds credentials.
      try {
        const probe = await fetch(`${BACKEND_URL}/api/user/profile`);
        if (probe.ok) {
          console.log('🍪 Active session cookie detected');
          // Mark the in-memory auth-token sentinel so authenticationStatus
          // flips to true. Value is opaque — only its truthiness matters now.
          setAuthToken('cookie');
          const userData = await probe.json();
          const user = {
            id: userData.user_id,
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            tier: userData.tier,
            subscription_status: userData.subscription_status,
            trial_ends_at: userData.trial_ends_at,
            created_at: userData.created_at,
          };
          setUser(user);
          console.log('✅ User restored from cookie session:', user.email);
        } else if (probe.status === 401 || probe.status === 403) {
          console.log('🚫 No active session');
        } else {
          console.warn('⚠️ Unexpected status from session probe:', probe.status);
        }
      } catch (err) {
        console.error('❌ Session probe failed:', err);
      }

      console.log('✅ Auth initialization complete');
      setAuthLoading(false);
    };
    
    initAuth();

    // Make utility functions globally available
    window.scrollToTop = VoxSmythUtils.scrollToTop;
    window.VoxSmythUtils = VoxSmythUtils;
  }, [fetchUserData]);

  // Memoized authentication status to prevent excessive checks
  const authenticationStatus = useMemo(() => {
    const hasToken = !!authToken;
    const hasUser = !!user;
    const result = hasToken && hasUser;
    return result;
  }, [authToken, user]);

  // Helper function to get current authentication state
  const getAuthState = useCallback(() => {
    return {
      isAuthenticated: authenticationStatus,
      user,
      authToken,
      userTier: user?.tier || 'free'
    };
  }, [authenticationStatus, user, authToken]);

  const logout = useCallback(() => {
    console.log('🚪 Logging out user');
    tokenService.clearToken();
    setAuthToken(null);
    setUser(null);
    console.log('🏠 Navigating to home after logout');
    navigate('/');
  }, [navigate]);

  const handleSuccessfulAuth = useCallback(async (authData) => {
    console.log('🔐 handleSuccessfulAuth called with:', authData);
    
    const token = authData.token || authData.access_token;
    if (!token) {
      console.error('❌ No token found in auth data');
      return;
    }
    
    // Phase 2: the auth cookie is already set by the backend. We don't need
    // to stash the token anywhere on the client. Just flip the in-memory
    // sentinel so authenticationStatus turns true.
    setAuthToken('cookie');
    
    if (authData.user) {
      console.log('👤 Setting user data from auth response:', authData.user);
      setUser(authData.user);
      
      // Add a small delay to ensure React state updates are complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if this is OAuth login (Google or Apple)
      if (authData.user.provider === 'google' || authData.user.provider === 'apple') {
        console.log('🔑 OAuth login detected, redirecting to success page');
        // Mark this as a recent OAuth login to prevent immediate redirects
        sessionStorage.setItem('recentOAuthLogin', Date.now().toString());
        navigate('/oauth-success');
      } else {
        // Regular email/password login
        console.log('🏠 Navigating to library (regular login)');
        navigate('/library');
      }
    } else {
      console.log('⚠️ No user data in auth response, fetching from profile endpoint...');
      
      // Fetch user data and then navigate
      try {
        await fetchUserData(token);
        // Add delay for state updates
        await new Promise(resolve => setTimeout(resolve, 100));
        // For cases without provider info, go to library
        console.log('🏠 Navigating to library (after profile fetch)');
        navigate('/library');
      } catch (error) {
        console.error('❌ Failed to fetch user data after login:', error);
        // Navigate anyway, but authentication might not persist
        navigate('/library');
      }
    }
  }, [navigate, fetchUserData]);

  // Voice configuration helpers
  const updateVoiceConfiguration = useCallback((key, value) => {
    setVoiceConfiguration(prev => {
      const updated = { ...prev };
      if (key === 'narrator') {
        updated.narratorVoice = value;
      } else if (key === 'characters') {
        updated.characterAssignments = { ...updated.characterAssignments, ...value };
      } else if (key === 'character') {
        updated.characterAssignments = { ...updated.characterAssignments, ...value };
      }
      updated.hasChanges = true;
      return updated;
    });
  }, []);

  // Audio playback functions for Enhanced Landing with timepoint persistence
  const currentTimepointRef = useRef(0);
  const [currentTimepoint, setCurrentTimepoint] = useState(0);
  const [demoAudioElement, setDemoAudioElement] = useState(null);
  const generatedAudioCacheRef = useRef(new Map());
  
  // Clear old cached audio with the outdated text
  useEffect(() => {
    // Clear cache entries that contain the old demo text
    const oldTextIdentifiers = [
      'This is a voice sample for Voxsmyth.',
      'voice sample for Voxsmyth'
    ];
    
    const currentCache = generatedAudioCacheRef.current;
    const keysToDelete = [];
    
    for (const [key, value] of currentCache.entries()) {
      // Check if this cache entry was created with the old text
      oldTextIdentifiers.forEach(oldText => {
        if (key.includes(oldText) || (typeof value === 'string' && value.includes(oldText))) {
          keysToDelete.push(key);
        }
      });
    }
    
    keysToDelete.forEach(key => {
      currentCache.delete(key);
      console.log('Cleared old cached audio:', key);
    });
  }, []);
  
  // Cleanup individual voice audio on unmount
  useEffect(() => {
    return () => {
      if (individualVoiceAudio) {
        individualVoiceAudio.pause();
        setIndividualVoicePlaying(null);
      }
    };
  }, [individualVoiceAudio]);
  
  // Cleanup audio element on unmount or change
  useEffect(() => {
    return () => {
      if (demoAudioElement && demoAudioElement._cleanup) {
        demoAudioElement._cleanup();
        demoAudioElement.pause();
        demoAudioElement.src = '';
        demoAudioElement.load();
      }
    };
  }, [demoAudioElement]);
  
  const switchVoice = useCallback((voiceId, shouldPlayIndividualDemo = false) => {
    console.log('Voice switched to:', voiceId);
    
    // Update the selected voice (for visual highlighting)
    setCurrentVoice(voiceId);
    
    // Handle individual voice demo requests
    if (shouldPlayIndividualDemo) {
      console.log('Individual voice demo requested for:', voiceId);
      
      // If clicking the same voice that's currently playing, pause it
      if (individualVoicePlaying === voiceId && individualVoiceAudio) {
        console.log('Pausing individual voice demo for:', voiceId);
        
        // Save current timepoint to shared global timepoint
        if (individualVoiceAudio.currentTime) {
          const currentTime = individualVoiceAudio.currentTime;
          setIndividualVoiceTimepoint(currentTime);
          individualVoiceTimepointRef.current = currentTime;
          console.log('Saved shared timepoint:', currentTime, '(paused', voiceId, ')');
        }
        
        individualVoiceAudio.pause();
        setIndividualVoicePlaying(null);
        setLastSelectedVoice(voiceId); // Remember this voice for resume
        return;
      }
      
      // Stop any currently playing individual voice and save its timepoint to shared timepoint
      if (individualVoiceAudio && individualVoicePlaying) {
        if (individualVoiceAudio.currentTime) {
          const currentTime = individualVoiceAudio.currentTime;
          setIndividualVoiceTimepoint(currentTime);
          individualVoiceTimepointRef.current = currentTime;
          console.log('Switching voices - saved shared timepoint:', currentTime, '(from', individualVoicePlaying, 'to', voiceId, ')');
        }
        individualVoiceAudio.pause();
        setIndividualVoicePlaying(null);
      }
      
      // All voices use the shared timepoint (where the previous voice left off)
      const startTimepoint = individualVoiceTimepointRef.current;
      
      if (startTimepoint > 0) {
        console.log('Starting', voiceId, 'from shared timepoint:', startTimepoint, '(where previous voice left off)');
      } else {
        console.log('Starting', voiceId, 'from beginning (no previous timepoint)');
      }
      
      // Set the last selected voice
      setLastSelectedVoice(voiceId);
      
      // Call playIndividualVoiceDemo with shared timepoint
      if (window.playIndividualVoiceDemo) {
        window.playIndividualVoiceDemo(voiceId, startTimepoint);
      } else {
        // Set a flag that will be picked up after playIndividualVoiceDemo is available
        window.pendingIndividualVoiceDemo = {
          voiceId: voiceId,
          timepoint: startTimepoint
        };
        console.log('Queued individual voice demo for:', voiceId, 'at shared timepoint:', startTimepoint);
      }
    }
  }, [individualVoicePlaying, individualVoiceAudio, individualVoiceTimepointRef]);

  const generateDemoAudio = useCallback(async (voiceId, customText = null) => {
    // Improved cache key that includes the actual text content for better caching
    const textHash = customText ? customText.substring(0, 20).replace(/\s+/g, '') : 'default';
    const cacheKey = `${voiceId}-${textHash}`;
    if (generatedAudioCacheRef.current.has(cacheKey)) {
      console.log('Using cached demo audio for voice:', voiceId, 'with text hash:', textHash);
      return generatedAudioCacheRef.current.get(cacheKey);
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    console.log('🚨 DEBUG - Environment variable value:', backendUrl);
    console.log('🚨 DEBUG - All env vars:', Object.keys(process.env).filter(k => k.startsWith('REACT_APP')));
    
    if (!backendUrl || backendUrl === 'undefined') {
      console.error('🚨 CRITICAL: REACT_APP_BACKEND_URL is undefined!');
      console.log('🚨 Using hardcoded fallback');
      // Force the correct URL
      const finalUrl = 'https://voxsmyth.com';
      console.log('🚨 Final URL being used:', finalUrl);
    } else {
      console.log('🚨 Environment variable loaded correctly:', backendUrl);
    }
    
    const finalBackendUrl = backendUrl || 'https://voxsmyth.com';
    const fullUrl = `${finalBackendUrl}/api/tts/${voiceId}/demo`;
    console.log('🚨 FULL URL:', fullUrl);

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: customText || MULTI_VOICE_DEMO_CONFIG.demoText
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Cache the generated audio using ref to avoid state updates
      generatedAudioCacheRef.current.set(cacheKey, audioUrl);
      
      return audioUrl;
    } catch (error) {
      console.error('Failed to generate demo audio:', error);
      // Fallback to regular voice demo sample with the same URL logic
      const fallbackBackendUrl = process.env.REACT_APP_BACKEND_URL || 'https://voxsmyth.com';
      console.log('🚨 FALLBACK URL:', `${fallbackBackendUrl}/api/voices/${voiceId}/sample/demo`);
      return `${fallbackBackendUrl}/api/voices/${voiceId}/sample/demo`;
    }
  }, []); // Remove cache dependency to prevent re-creation

  // Separate function for playing individual voice card demos (placed after generateDemoAudio)
  const playIndividualVoiceDemo = useCallback(async (voiceId, startTimepoint = 0) => {
    console.log(`🎵 Starting individual voice demo for ${voiceId} at timepoint ${startTimepoint}`);
    
    // Prevent multiple simultaneous calls
    if (individualVoiceLoading) {
      console.log('Individual voice demo already loading, skipping...');
      return;
    }
    
    setIndividualVoiceLoading(true);
    
    try {
      // Stop any currently playing audio cleanly
      if (individualVoiceAudio) {
        individualVoiceAudio.pause();
        individualVoiceAudio.currentTime = 0;
        setIndividualVoiceAudio(null);
      }
      
      // Generate audio for individual voice (using Tale of Two Cities text)
      const audioUrl = await generateDemoAudio(voiceId, MULTI_VOICE_DEMO_CONFIG.individualVoiceText);
      
      // Create and configure audio with better error handling
      const audio = new Audio();
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      
      // Set up event listeners before setting src
      const handleCanPlayThrough = () => {
        console.log(`🎵 Audio ready for ${voiceId}`);
        
        // Set timepoint for continuation (more reliable timing)
        if (startTimepoint > 0) {
          // Use a small delay to ensure audio is fully ready
          setTimeout(() => {
            if (startTimepoint < audio.duration) {
              audio.currentTime = startTimepoint;
              console.log(`⏭️ Resuming ${voiceId} at ${startTimepoint}s`);
            } else {
              console.log(`⚠️ Timepoint ${startTimepoint}s exceeds duration ${audio.duration}s, starting from beginning`);
              audio.currentTime = 0;
            }
            
            // Set state after timepoint is set
            setIndividualVoiceAudio(audio);
            setIndividualVoicePlaying(voiceId);
            setIndividualVoiceLoading(false);
            
            // Start playback
            audio.play().catch(error => {
              console.error('Audio playback failed:', error);
              setIndividualVoicePlaying(null);
              setIndividualVoiceLoading(false);
            });
          }, 100);
        } else {
          console.log(`▶️ Starting ${voiceId} from beginning`);
          
          // Set state before playing for beginning playback
          setIndividualVoiceAudio(audio);
          setIndividualVoicePlaying(voiceId);
          setIndividualVoiceLoading(false);
          
          // Start playback
          audio.play().catch(error => {
            console.error('Audio playback failed:', error);
            setIndividualVoicePlaying(null);
            setIndividualVoiceLoading(false);
          });
        }
      };
      
      const handleEnded = () => {
        console.log(`⏹️ Audio ended for ${voiceId}`);
        setIndividualVoicePlaying(null);
        // Keep timepoint and lastSelectedVoice for potential resume
        console.log(`✅ ${voiceId} demo ended - timepoint preserved for resume`);
      };
      
      const handleError = (e) => {
        console.error(`❌ Audio error for ${voiceId}:`, e);
        setIndividualVoicePlaying(null);
        setIndividualVoiceLoading(false);
        // Keep lastSelectedVoice for potential retry
      };
      
      const handleTimeUpdate = () => {
        if (audio.currentTime && !audio.paused) {
          // Throttle updates to prevent excessive re-renders
          const now = Date.now();
          if (!audio._lastUpdate || now - audio._lastUpdate > 200) {
            audio._lastUpdate = now;
            const currentTime = audio.currentTime;
            // Update shared global timepoint (used by all voices)
            setIndividualVoiceTimepoint(currentTime);
            individualVoiceTimepointRef.current = currentTime;
          }
        }
      };
      
      // Add all event listeners
      audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      audio.addEventListener('ended', handleEnded, { once: true });
      audio.addEventListener('error', handleError, { once: true });
      audio.addEventListener('timeupdate', handleTimeUpdate, { passive: true });
      
      // Set source and load
      audio.src = audioUrl;
      audio.load();
      
    } catch (error) {
      console.error('Error setting up individual voice demo:', error);
      setIndividualVoicePlaying(null);
      setIndividualVoiceLoading(false);
    }
  }, [generateDemoAudio, individualVoiceTimepointRef, individualVoiceLoading, individualVoiceAudio]);

  // Process queued individual voice demo requests
  useEffect(() => {
    const processQueuedDemo = async () => {
      if (window.pendingIndividualVoiceDemo && !individualVoiceLoading) {
        const { voiceId, timepoint } = window.pendingIndividualVoiceDemo;
        delete window.pendingIndividualVoiceDemo;
        
        console.log('Processing queued individual voice demo:', voiceId, 'at timepoint:', timepoint);
        await playIndividualVoiceDemo(voiceId, timepoint);
      }
    };

    // Check for queued requests periodically
    const interval = setInterval(processQueuedDemo, 100);
    
    return () => clearInterval(interval);
  }, [playIndividualVoiceDemo, individualVoiceLoading]);

  // Make playIndividualVoiceDemo available globally to fix initialization order issues
  useEffect(() => {
    window.playIndividualVoiceDemo = playIndividualVoiceDemo;
    return () => {
      delete window.playIndividualVoiceDemo;
    };
  }, [playIndividualVoiceDemo]);

  const playDemoWithTimepoint = useCallback(async (voiceId, customText = null) => {
    try {
      setLoading(true);
      console.log('Playing demo with timepoint persistence for voice:', voiceId, 'at time:', currentTimepointRef.current);
      
      // Stop and cleanup any currently playing audio
      if (demoAudioElement) {
        currentTimepointRef.current = demoAudioElement.currentTime;
        setCurrentTimepoint(currentTimepointRef.current);
        demoAudioElement.pause();
        demoAudioElement.src = ''; // Clear the source to stop loading
        demoAudioElement.load(); // Reset the element
      }

      // Generate or get cached audio for this voice
      const audioUrl = await generateDemoAudio(voiceId, customText);
      const audio = new Audio(audioUrl);
      
      let isAudioActive = true; // Flag to prevent stale callbacks
      
      // Store reference to current demo audio
      setDemoAudioElement(audio);
      
      // Create event handlers with cleanup flag checks
      const handleLoadStart = () => {
        if (isAudioActive) {
          console.log('Loading demo audio...');
        }
      };
      
      const handleCanPlayThrough = () => {
        if (!isAudioActive) return;
        
        console.log('Demo audio ready to play');
        // Set the timepoint for continuity
        audio.currentTime = currentTimepointRef.current;
        
        // Use requestAnimationFrame to defer state updates and prevent render loops
        requestAnimationFrame(() => {
          if (isAudioActive) {
            setLoading(false);
            setIsPlaying(true);
          }
        });
      };
      
      const handleEnded = () => {
        if (!isAudioActive) return;
        
        console.log('Demo audio playback ended');
        requestAnimationFrame(() => {
          if (isAudioActive) {
            setIsPlaying(false);
            setLoading(false);
            currentTimepointRef.current = 0;
            setCurrentTimepoint(0);
          }
        });
      };

      const handleTimeUpdate = () => {
        if (isAudioActive && Math.abs(audio.currentTime - currentTimepointRef.current) > 0.1) {
          currentTimepointRef.current = audio.currentTime;
          // Throttle timepoint updates to prevent excessive re-renders
          if (!audio._throttleTimeout) {
            audio._throttleTimeout = setTimeout(() => {
              if (isAudioActive) {
                setCurrentTimepoint(audio.currentTime);
              }
              audio._throttleTimeout = null;
            }, 100);
          }
        }
      };
      
      const handleError = (e) => {
        if (!isAudioActive) return;
        
        console.error('Demo audio playback error:', e);
        requestAnimationFrame(() => {
          if (isAudioActive) {
            setIsPlaying(false);
            setLoading(false);
            alert('Demo audio not available. Please try another voice.');
          }
        });
      };

      // Add event listeners with proper options
      audio.addEventListener('loadstart', handleLoadStart, { once: true });
      audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      audio.addEventListener('ended', handleEnded, { once: true });
      audio.addEventListener('timeupdate', handleTimeUpdate, { passive: true });
      audio.addEventListener('error', handleError, { once: true });

      // Store cleanup function
      audio._cleanup = () => {
        isAudioActive = false;
        if (audio._throttleTimeout) {
          clearTimeout(audio._throttleTimeout);
          audio._throttleTimeout = null;
        }
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('error', handleError);
      };

      await audio.play();
      
    } catch (error) {
      console.error('Failed to play demo audio:', error);
      setLoading(false);
      setIsPlaying(false);
      alert('Failed to play demo audio. Please try again.');
    }
  }, [generateDemoAudio, demoAudioElement]);

  // Fallback to original sample-based playback for compatibility
  const playVoiceSample = useCallback(async (voiceId) => {
    try {
      setLoading(true);
      console.log('Playing voice sample for:', voiceId);
      
      // Stop any currently playing audio
      if (window.currentVoiceAudio) {
        window.currentVoiceAudio.pause();
        window.currentVoiceAudio.currentTime = 0;
      }

      const sampleUrl = `${process.env.REACT_APP_BACKEND_URL}/api/voices/${voiceId}/sample/demo`;
      const audio = new Audio(sampleUrl);
      
      // Store reference to current audio
      window.currentVoiceAudio = audio;
      
      audio.addEventListener('loadstart', () => {
        console.log('Loading voice sample...');
      });
      
      audio.addEventListener('canplaythrough', () => {
        console.log('Voice sample ready to play');
        setLoading(false);
        setIsPlaying(true);
      });
      
      audio.addEventListener('ended', () => {
        console.log('Voice sample playback ended');
        setIsPlaying(false);
        setLoading(false);
      });
      
      audio.addEventListener('error', (e) => {
        console.error('Voice sample playback error:', e);
        setIsPlaying(false);
        setLoading(false);
        alert('Voice sample not available. Please try another voice.');
      });

      await audio.play();
      
    } catch (error) {
      console.error('Failed to play voice sample:', error);
      setLoading(false);
      setIsPlaying(false);
      alert('Failed to play voice sample. Please try again.');
    }
  }, []);

  const startMultiVoiceDemo = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Starting multi-voice demo');
      
      // Reset timepoint for multi-voice demo
      currentTimepointRef.current = 0;
      setCurrentTimepoint(0);
      
      // Generate audio for all voices used in the multi-voice demo
      console.log('🎭 Multi-voice demo: Generating audio for', MULTI_VOICE_DEMO_CONFIG.highlights.length, 'segments');
      const voicePromises = MULTI_VOICE_DEMO_CONFIG.highlights.map(async (highlight, index) => {
        const voiceId = MULTI_VOICE_DEMO_CONFIG.voiceAssignments[highlight.character]?.id;
        console.log(`🎭 Segment ${index + 1}: ${highlight.character} (${voiceId}) - "${highlight.text.substring(0, 30)}..."`);
        if (voiceId && highlight.text) {
          return generateDemoAudio(voiceId, highlight.text);
        }
        return null;
      });
      
      const audioUrls = await Promise.all(voicePromises);
      
      // Create a sequence of audio elements
      let currentSegment = 0;
      
      const playNextSegment = () => {
        if (currentSegment < MULTI_VOICE_DEMO_CONFIG.highlights.length) {
          const highlight = MULTI_VOICE_DEMO_CONFIG.highlights[currentSegment];
          const voiceId = MULTI_VOICE_DEMO_CONFIG.voiceAssignments[highlight.character]?.id;
          const audioUrl = audioUrls[currentSegment];
          
          if (audioUrl && voiceId) {
            console.log(`Playing segment ${currentSegment + 1}/${MULTI_VOICE_DEMO_CONFIG.highlights.length}:`, highlight.character);
            
            // Update current voice to show which one is playing
            setCurrentVoice(voiceId);
            
            const audio = new Audio(audioUrl);
            setDemoAudioElement(audio);
            
            audio.oncanplaythrough = () => {
              setLoading(false);
              setIsPlaying(true);
              audio.play().catch(console.error);
            };
            
            audio.onended = () => {
              currentSegment++;
              setTimeout(() => {
                playNextSegment(); // Small pause between segments
              }, 500);
            };
            
            audio.onerror = (error) => {
              console.error('Audio playback error:', error);
              currentSegment++;
              playNextSegment();
            };
          } else {
            currentSegment++;
            playNextSegment();
          }
        } else {
          // Multi-voice demo completed
          setIsPlaying(false);
          setLoading(false);
          console.log('Multi-voice demo completed');
        }
      };
      
      // Start playing the first segment
      playNextSegment();
      
    } catch (error) {
      console.error('Failed to start multi-voice demo:', error);
      setLoading(false);
      setIsPlaying(false);
    }
  }, [generateDemoAudio]);

  const togglePlayback = useCallback((isMultiVoice = false) => {
    if (isPlaying) {
      // Stop current demo audio and save timepoint
      if (demoAudioElement) {
        currentTimepointRef.current = demoAudioElement.currentTime;
        setCurrentTimepoint(currentTimepointRef.current);
        demoAudioElement.pause();
      }
      // Also stop any regular voice audio for compatibility
      if (window.currentVoiceAudio) {
        window.currentVoiceAudio.pause();
        window.currentVoiceAudio.currentTime = 0;
      }
      setIsPlaying(false);
      console.log('Demo playback stopped at timepoint:', currentTimepointRef.current);
    } else {
      if (isMultiVoice) {
        // Start multi-voice demo with voice switching based on highlights
        startMultiVoiceDemo();
      } else {
        // Play current voice demo with timepoint persistence
        if (currentVoice) {
          playDemoWithTimepoint(currentVoice);
        } else {
          // Fallback to first available voice
          const firstVoice = Object.values(MULTI_VOICE_DEMO_CONFIG.voiceAssignments)[0];
          if (firstVoice) {
            playDemoWithTimepoint(firstVoice.id);
          } else {
            console.warn('No voice available for demo playback');
          }
        }
      }
    }
  }, [isPlaying, currentVoice, demoAudioElement, startMultiVoiceDemo, playDemoWithTimepoint]);

  // Demo text for landing page
  const demoText = "Experience the magic of multi-voice storytelling with VoxSmyth. Switch between narrator voices in real-time and give each character their own unique voice.";

  // Scroll to top utility
  const scrollToTop = VoxSmythUtils.scrollToTop;

  // Classic components with navigation props
  const getClassicProps = useCallback(() => ({
    user,
    authToken,
    userTier: user?.tier || 'free',
    isAuthenticated: authenticationStatus,
    getAuthState,
    onNavigateHome: navigateToHome,
    onNavigateAuth: navigateToAuth,
    onNavigateLibrary: navigateToLibrary,
    onNavigateDashboard: navigateToDashboard,
    onNavigatePricing: navigateToPricing,
    onNavigateVoices: navigateToVoices,
    onNavigateVoiceActorPortal: navigateToVoiceActorPortal,
    onNavigateMenu: navigateToMenu,
    onNavigateToPlayer: navigateToPlayer,
    onBack: navigateToHome,
    onLogout: logout,
    promoCode,
    setPromoCode,
    currentVoice,
    setCurrentVoice,
    currentBook,
    setCurrentBook,
    currentChapter,
    setCurrentChapter,
    voiceConfiguration,
    updateVoiceConfiguration,
    // Enhanced Landing page specific props
    isPlaying,
    loading,
    switchVoice,
    togglePlayback,
    demoText,
    scrollToTop,
    // Individual voice demo props
    individualVoicePlaying,
    individualVoiceLoading,
    individualVoiceTimepoint
  }), [
    user, authToken, authenticationStatus, getAuthState, navigateToHome, navigateToAuth, navigateToLibrary,
    navigateToDashboard, navigateToPricing, navigateToVoices, navigateToVoiceActorPortal, navigateToMenu,
    navigateToPlayer, logout, promoCode, setPromoCode, currentVoice, setCurrentVoice,
    currentBook, setCurrentBook, currentChapter, setCurrentChapter,
    voiceConfiguration, updateVoiceConfiguration, isPlaying, loading,
    switchVoice, togglePlayback, demoText, scrollToTop, individualVoicePlaying, individualVoiceLoading, individualVoiceTimepoint
  ]);

  // Loading screen
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1A202C 0%, #2C3A4C 100%)',
        color: 'white',
        fontFamily: 'Georgia, serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            <VoxSmythIcons.VoxSmythSigil size={32} color="#D7B377" animated={true} />
          </div>
          <div>Loading VoxSmyth...</div>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <PlayerProvider>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}>
          <Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#1A202C',color:'#D7B377',fontFamily:'DM Serif Display,serif',fontSize:'20px'}}>Loading...</div>}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
          <Routes>
            {/* OAuth Login Success Route - BEFORE OTHER ROUTES */}
            <Route 
              path="/oauth-success" 
              element={
                <OAuthLoginSuccess 
                  isAuthenticated={authenticationStatus}
                  authToken={authToken}
                  user={user}
                />
              } 
            />

            {/* Test OAuth Route */}
            <Route 
              path="/test-oauth" 
              element={
                <div style={{
                  minHeight: '100vh',
                  background: '#2C3A4C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#D7B377',
                  fontSize: '2rem'
                }}>
                  TEST: OAuth route working!
                </div>
              } 
            />

            {/* Home / Landing */}
            <Route 
              path="/" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedLanding}
                  setCurrentPage={navigate}
                  authLoading={authLoading}
                  user={user}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Authentication */}
            <Route 
              path="/auth" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedAuthPage}
                  onSuccessfulAuth={handleSuccessfulAuth}
                  {...getClassicProps()}
                />
              } 
            />


            {/* Logout Route */}
            <Route 
              path="/logout" 
              element={
                <LogoutHandler logout={logout} />
              } 
            />

            {/* User Menu */}
            <Route 
              path="/menu" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedUserMenu}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Account Management */}
            <Route 
              path="/account" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedAccountManagement}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Library */}
            <Route 
              path="/library" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedLibrary}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Voice Library */}
            <Route 
              path="/voices" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedVoiceLibrary}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Emporium - Marketplace */}
            <Route 
              path="/emporium" 
              element={
                <EnhancedEmporium 
                  user={user}
                  authToken={authToken}
                />
              } 
            />

            {/* Pricing */}
            <Route 
              path="/pricing" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedPricing}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Try Free — dedicated low-friction free-trial checkout (ad landing) */}
            <Route 
              path="/try-free" 
              element={<TryFreePage user={user} />} 
            />

            {/* How It Works — animated product walkthrough */}
            <Route 
              path="/how-it-works" 
              element={<HowItWorksPage />} 
            />

            {/* Contact Support */}
            <Route 
              path="/contact" 
              element={<ContactSupportPage user={user} />} 
            />

            {/* Creator Studio */}
            <Route 
              path="/creator" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedCreatorStudio}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Creator Studio - Alternative Route */}
            <Route 
              path="/creator-studio" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedCreatorStudio}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Documentation */}
            <Route 
              path="/docs" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedDocumentation}
                  {...getClassicProps()}
                />
              } 
            />

            {/* FAQ - direct route for SEO */}
            <Route 
              path="/faq" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={(props) => <EnhancedDocumentation {...props} initialSection="faq" />}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Referral */}
            <Route 
              path="/referral" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedReferralPage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Player */}
            <Route 
              path="/player/:bookId?" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedPlayerPage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Voice Actor Portal */}
            <Route 
              path="/voice-actor-portal" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedVoiceActorPortal}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Invitation Choice Page */}
            <Route 
              path="/join-voxsmyth" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={InvitationChoicePage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Invitation Request Page */}
            <Route 
              path="/request-invitation" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={InvitationIntakePage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Creator Intake Page */}
            <Route 
              path="/creator-intake" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={CreatorIntakePage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Forgot Password Page */}
            <Route 
              path="/forgot-password" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={ForgotPasswordPage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Reset Password Page */}
            <Route 
              path="/reset-password" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={ResetPasswordPage}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Admin Portal */}
            <Route 
              path="/admin" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedAdminPortal}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Static Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage onNavigateHome={navigateToHome} />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage onNavigateHome={navigateToHome} />} />
            <Route path="/acceptable-use-policy" element={<AcceptableUsePolicyPage onBack={navigateToHome} />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage onNavigateHome={navigateToHome} />} />
            <Route path="/voice-actor/:slug" element={<VoiceActorProfile />} />
            <Route path="/voice/:voiceId" element={<VoiceSharePage />} />
            <Route path="/marketplace" element={<VocalMarketplace />} />
            
            {/* Payment Pages */}
            <Route 
              path="/payment/:tier?" 
              element={
                <EnhancedPaymentPortal
                  user={user}
                  authToken={authToken}
                />
              } 
            />
            <Route 
              path="/payment/success" 
              element={
                <PaymentSuccess
                  user={user}
                  authToken={authToken}
                />
              } 
            />
            <Route 
              path="/payment/cancel" 
              element={<PaymentCancel />} 
            />
            <Route path="/payment-success" element={<PaymentSuccessPage onBackToLibrary={navigateToLibrary} />} />
            <Route path="/payment-cancel" element={<PaymentCancelPage onBackToPricing={navigateToPricing} />} />
            <Route path="/payment-history" element={<PaymentHistoryPage onBackToLibrary={navigateToLibrary} />} />

            {/* Admin & Voice Actor Pages */}
            <Route path="/admin-portal" element={
              <EnhancedAdminPortal 
                user={user}
                userTier={user?.tier}
                authToken={authToken}
                navigate={navigate}
              />
            } />
            <Route path="/voice-actor-dashboard" element={<VoiceActorDashboard onBackToLibrary={navigateToLibrary} />} />
            <Route path="/voice-cloning-guide" element={<VoiceCloningGuide user={user} />} />

            {/* Coming Soon - Catch-all for unimplemented features */}
            <Route 
              path="/coming-soon/:feature" 
              element={
                <EnhancedComingSoon 
                  title={comingSoonTitle || "Feature in Development"}
                  onNavigateHome={navigateToHome}
                />
              } 
            />

            {/* Coming Soon */}
            <Route 
              path="/coming-soon" 
              element={
                <EnhancedPageWrapper
                  enhancedComponent={EnhancedComingSoon}
                  title={comingSoonTitle}
                  {...getClassicProps()}
                />
              } 
            />

            {/* Icon Demo */}
            <Route 
              path="/icon-demo" 
              element={<VoxSmythIconDemo />} 
            />

            {/* 404 / Fallback */}
            <Route 
              path="*" 
              element={
                <VoxSmythErrorPage
                  title="Page Not Found"
                  subtitle="This page seems to have wandered off the script"
                  message="The story you're looking for has taken an unexpected plot twist. Let's get you back to where the magic happens."
                  onNavigateHome={navigateToHome}
                  showGoBack={true}
                />
              } 
            />
          </Routes>
            </motion.div>
          </AnimatePresence>
          </Suspense>
        </div>

        {/* Enhanced Global Footer */}
        <EnhancedFooter 
          user={user}
          onNavigatePricing={navigateToPricing}
          onLogout={logout}
        />

        {/* P0 Mobile-native PWA chrome (CSS-gated to ≤768px). */}
        <MobileBottomNav />

        {/* Trial countdown pill — shown app-wide while subscription is trialing */}
        <TrialCountdownBadge user={user} />

        {/* One-time legal re-acceptance modal on updated Terms/Privacy */}
        <LegalUpdateBanner user={user} />

        {/* Cookie consent (GDPR/CCPA) — Consent Mode v2 wired */}
        <CookieConsentBanner />
      </div>
      </PlayerProvider>
    </GoogleOAuthProvider>
  );
};

export default AppRouter;